import { useSyncExternalStore } from 'react';

export const IMPROVED_LAYOUT_STORAGE_KEY = 'Layout.improvedLayout';
const CHANGE_EVENT = 'improved-layout-changed';

/**
 * When on, the page content is capped at CONTENT_MAX_WIDTH and sits against the menu, leaving the
 * rest of the row for side panels and the watermark. Off by default — the content then spans the
 * whole width of the shell, as it did before the layout rework.
 *
 * Kept in its own store rather than useLocalStorage because the switch lives on the profile page
 * while the reader is the layout, which stays mounted across navigation: useLocalStorage does not
 * sync between components, so the layout would not react until a reload.
 */
export const readImprovedLayout = (): boolean => {
  try {
    return localStorage.getItem(IMPROVED_LAYOUT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const writeImprovedLayout = (v: boolean) => {
  try {
    localStorage.setItem(IMPROVED_LAYOUT_STORAGE_KEY, v ? '1' : '0');
  } catch {
    // ignore
  }
  // localStorage writes don't fire 'storage' in the same tab — broadcast manually.
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const subscribeImprovedLayout = (cb: () => void): (() => void) => {
  const onStorage = (e: StorageEvent) => {
    if (e.key === IMPROVED_LAYOUT_STORAGE_KEY) cb();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, cb);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, cb);
  };
};

export const useImprovedLayout = (): boolean => useSyncExternalStore(subscribeImprovedLayout, readImprovedLayout, () => false);
